<script setup lang="ts">
import BaseHeader from '@/components/BaseHeader.vue';
import BaseSidebar from '@/components/BaseSidebar.vue';
import { useLayoutStore } from '@/stores/layout';
import { useSettingsStore } from '@/stores/settingsStore';
import { useThemeStore } from '@/stores/themeStore';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import { initializeLocale } from './i18n';
import { useUserStore } from './stores/user';
import Notifications from './components/Notifications.vue';
import { AuthService } from './services/authService';
import SelectTenantModal from './components/benutzer/SelectTenantModal.vue';
import TenantService from '@/services/tenantService';
const layoutStore = useLayoutStore();
const route = useRoute();
const settingsStore = useSettingsStore();
const userStore = useUserStore();
const themeStore = useThemeStore();
const seedMonth = ref<number>(1);
const authService = new AuthService();
const tenantService = new TenantService();
const apiOnline = ref(true);

// Prüfen, ob wir uns auf der Home oder Login Seite befinden
const isPublicRoute = computed(() => {
  return ['home', 'login', 'not-found'].includes(route.name as string) || route.meta.isPublic;
});

onMounted(async () => {
  themeStore.initTheme();
  await settingsStore.loadSettings();
  initializeLocale();

  (window as any).copyTenant = (oldTenant: string, newTenant: string) => {
    tenantService.copy({ oldTenant, newTenant });
  };

  (window as any).refreshToken = () => {
    authService.refreshToken();
  };

});

onUnmounted(() => {
  delete (window as any).copyTenant;
  delete (window as any).refreshToken;
});
</script>

<template>
  <div class="min-h-screen w-full flex flex-col bg-slate-50 dark:bg-slate-900">
    <!-- Header und Sidebar nur anzeigen, wenn wir nicht auf einer öffentlichen Route sind -->
    <template v-if="!isPublicRoute">
      <SelectTenantModal v-if="!userStore.tenant" />
      <template v-else>
        <BaseHeader />
        <BaseSidebar />
        <main
          class="flex-1 bg-slate-50 dark:bg-slate-900 p-8 transition-all duration-300 mt-12"
          :style="{
            marginLeft: layoutStore.isExpanded ? '256px' : '72px',
            width: `calc(100% - ${layoutStore.isExpanded ? '256px' : '72px'})`,
          }"
        >
          <RouterView />
        </main>
      </template>
    </template>

    <!-- Auf öffentlichen Routen nur den RouterView anzeigen -->
    <template v-else>
      <RouterView />
    </template>

    <Notifications />
  </div>
</template>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap');

#app {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-color);
  color: var(--text-color);
}

:root {
  --bg-color: #f8fafc;
  --text-color: #1e293b;
  --toast-border-radius: 8px;
  --toast-width: 389px;
  --toast-height: 79px;
  --toast-padding: 12px 24px;
  --toast-font-family: 'Inter', sans-serif;
  --toast-gap: 6px;
  --toast-success-color: #0f766e;
  --toast-error-color: #991b1b;
}

.dark {
  --bg-color: #0f172a;
  --text-color: #f1f5f9;
}

[data-sonner-toaster] {
  font-family: var(--toast-font-family);
}

[data-sonner-toast] {
  display: flex !important;
  width: var(--toast-width) !important;
  height: var(--toast-height) !important;
  padding: var(--toast-padding) !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: var(--toast-gap) !important;
  shrink: 0 !important;
  border-radius: var(--toast-border-radius) !important;
  background: #f8fafc !important;
  box-shadow: 0px 12px 30px 0px rgba(15, 23, 42, 0.08) !important;
}

.dark [data-sonner-toast] {
  background: #1e293b !important;
  box-shadow: 0px 12px 30px 0px rgba(0, 0, 0, 0.2) !important;
}

[data-sonner-toast][data-type="success"] {
  border-left: 5px solid var(--toast-success-color) !important;
}

[data-sonner-toast][data-type="error"] {
  border-left: 5px solid var(--toast-error-color) !important;
}

[data-sonner-toast] [data-title] {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #1e293b !important;
  line-height: 20px !important;
  margin: 0 !important;
  padding: 0 !important;
}

.dark [data-sonner-toast] [data-title] {
  color: #f1f5f9 !important;
}

[data-sonner-toast]::before {
  content: 'CEEMAS Benachrichtigung';
  display: block;
  color: #64748b;
  font-size: 10px;
  font-weight: 400;
  line-height: 14px;
}

.dark [data-sonner-toast]::before {
  color: #94a3b8;
}

[data-sonner-toast] [data-close-button] {
  display: none !important;
}

[data-sonner-toast] [data-icon] {
  display: none !important;
}
</style>

